import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Server, Trash2, PlugZap, Pencil, CheckCircle2, XCircle, Zap, ExternalLink } from "lucide-react";
import { toast as _toast } from "sonner";

const ENC = [
  { value: "starttls", label: "STARTTLS" },
  { value: "ssl", label: "SSL/TLS" },
  { value: "none", label: "None" },
];

const PROVIDERS = [
  { value: "custom", label: "Custom SMTP host" },
  { value: "turbo_smtp", label: "Turbo SMTP (managed)" },
];

const EMPTY = { name: "", provider: "custom", host: "", port: 587, encryption: "starttls", username: "", password: "", from_email: "", from_name: "", daily_limit: 5000, hourly_limit: 500 };

export default function SmtpProfiles() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, company_id: "" });
  const [editingId, setEditingId] = useState(null);
  const [turboStatus, setTurboStatus] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [p, c, ts, d] = await Promise.all([
      api.get("/bulk/smtp-profiles", { params }),
      api.get("/companies"),
      api.get("/turbosmtp/status"),
      api.get("/turbosmtp/domains", { params }),
    ]);
    setItems(p.data);
    setCompanies(c.data);
    setTurboStatus(ts.data);
    setDomains(d.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const addDomain = async () => {
    if (!newDomain || !form.company_id) return;
    try {
      await api.post("/turbosmtp/domains", { company_id: activeCompanyId || form.company_id, domain: newDomain });
      _toast.success(`Whitelisted ${newDomain}`);
      setNewDomain("");
      load();
    } catch (e) { _toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeDomain = async (id) => {
    try { await api.delete(`/turbosmtp/domains/${id}`); _toast.success("Domain removed"); load(); }
    catch (e) { _toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openNew = () => { setForm({ ...EMPTY, company_id: activeCompanyId || companies[0]?.id || "" }); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, password: "" }); setEditingId(p.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { password, id: _id, ...patch } = form;
        if (password) patch.password = password;
        await api.patch(`/bulk/smtp-profiles/${editingId}`, patch);
        toast.success("Profile updated");
      } else {
        await api.post("/bulk/smtp-profiles", form);
        toast.success("Profile created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this SMTP profile?")) return;
    try { await api.delete(`/bulk/smtp-profiles/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const test = async (id) => {
    toast.info("Testing SMTP connection…");
    try {
      const { data } = await api.post(`/bulk/smtp-profiles/${id}/test`);
      data.ok ? toast.success("SMTP OK") : toast.error(`SMTP fail: ${data.info}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="smtp-profiles-page">
      {turboStatus?.configured && (
        <div className="rounded-2xl border border-emerald-brand/30 bg-emerald-brand/5 p-3 flex items-center gap-3" data-testid="turbo-status-banner">
          <div className="h-9 w-9 rounded-xl bg-emerald-brand/15 text-emerald-brand grid place-items-center"><Zap className="h-4 w-4" /></div>
          <div className="flex-1">
            <div className="text-sm font-medium">Turbo SMTP is connected</div>
            <div className="text-[11px] text-muted-foreground">Use the "Turbo SMTP (managed)" provider when creating a profile — no host/port required.</div>
          </div>
          <a href="https://serversmtp.com/turbo-api/" target="_blank" rel="noreferrer" className="text-[11px] text-emerald-brand hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> API docs</a>
        </div>
      )}

      {/* Whitelisted sender domains for Turbo SMTP */}
      <div className="rounded-2xl border border-border bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Whitelisted sender domains (Turbo SMTP)</div>
            <div className="text-[11px] text-muted-foreground">Only domains listed here can be used as "From" when sending via Turbo SMTP.</div>
          </div>
          <div className="flex items-center gap-1">
            <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="e.g. mail.example.com" className="h-8 w-56 rounded-sm text-xs" data-testid="turbo-domain-input" />
            <Button size="sm" onClick={addDomain} className="rounded-sm" data-testid="turbo-domain-add"><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {domains.length === 0 && <span className="text-[11px] text-muted-foreground">No domains yet — add one to enable Turbo SMTP sends.</span>}
          {domains.map((d) => (
            <span key={d.id} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded bg-emerald-brand/10 text-emerald-brand" data-testid={`turbo-domain-chip-${d.domain}`}>
              {d.domain}
              <button onClick={() => removeDomain(d.id)} className="hover:text-crimson"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={openNew} className="rounded-sm" data-testid="new-smtp-profile-button"><Plus className="h-4 w-4 mr-1.5" /> New SMTP profile</Button>
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-white">
        <table className="tbl w-full" data-testid="smtp-profiles-table">
          <thead><tr><th>Name</th><th>Host</th><th>From</th><th>Status</th><th>Limits</th><th></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`smtp-row-${p.id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-sm bg-emerald-brand/12 text-emerald-brand grid place-items-center">{p.provider === "turbo_smtp" ? <Zap className="h-3.5 w-3.5" /> : <Server className="h-3.5 w-3.5" />}</div>
                    <div>
                      <div className="font-medium flex items-center gap-1.5">
                        {p.name}
                        {p.provider === "turbo_smtp" && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-brand/15 text-emerald-brand uppercase">Turbo</span>}
                        {p.warmup_enabled && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-sun/25 text-yellow-700 uppercase">Warmup</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="font-mono text-xs">{p.provider === "turbo_smtp" ? "api.turbo-smtp.com" : `${p.host}:${p.port} · ${p.encryption}`}</td>
                <td className="text-xs">{p.from_name ? `${p.from_name} · ` : ""}{p.from_email}</td>
                <td>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${p.smtp_status === "ok" ? "bg-emerald-brand/12 text-emerald-brand" : p.smtp_status === "error" ? "bg-crimson/12 text-crimson" : "bg-muted text-muted-foreground"}`}>
                    {p.smtp_status === "ok" ? <><CheckCircle2 className="inline h-3 w-3 mr-0.5" />OK</> : p.smtp_status === "error" ? <><XCircle className="inline h-3 w-3 mr-0.5" />ERR</> : "—"}
                  </span>
                </td>
                <td className="font-mono text-[10px] text-muted-foreground">{p.hourly_limit}/hr · {p.daily_limit}/day</td>
                <td className="text-right space-x-1">
                  <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => test(p.id)} data-testid={`test-smtp-${p.id}`}><PlugZap className="h-3.5 w-3.5 mr-1" /> Test</Button>
                  {!p.warmup_enabled && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={async () => {
                      try { await api.post(`/bulk/smtp-profiles/${p.id}/warmup`, { days: 14, day1_cap: 50, max_cap: p.daily_limit || 5000 }); _toast.success("Warmup started"); load(); }
                      catch (e) { _toast.error(e?.response?.data?.detail || "Failed"); }
                    }} data-testid={`warmup-smtp-${p.id}`}><Zap className="h-3.5 w-3.5 mr-1" /> Warmup</Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => openEdit(p)} data-testid={`edit-smtp-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-xs text-crimson" onClick={() => remove(p.id)} data-testid={`delete-smtp-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No SMTP profiles yet. Add one to send bulk email.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="smtp-profile-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">{editingId ? "Edit" : "New"} SMTP profile</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editingId && (
              <F label="Company">
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
            <F label="Provider">
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger className="rounded-xl" data-testid="smtp-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Profile name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="smtp-name-input" /></F>
            {form.provider === "custom" && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <F label="Host"><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} required data-testid="smtp-host-input" /></F>
                  <F label="Port"><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} required /></F>
                  <F label="Encryption">
                    <Select value={form.encryption} onValueChange={(v) => setForm({ ...form, encryption: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ENC.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <F label="SMTP username"><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="smtp-username-input" /></F>
                <F label={editingId ? "Password (leave blank to keep)" : "SMTP password"}><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingId} data-testid="smtp-password-input" /></F>
              </>
            )}
            {form.provider === "turbo_smtp" && (
              <div className="rounded-xl border border-emerald-brand/30 bg-emerald-brand/5 p-3 text-xs space-y-1">
                <div className="font-medium flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-emerald-brand" /> Turbo SMTP managed provider</div>
                <div className="text-muted-foreground">Credentials come from server env (already configured). Select a From email from your whitelisted domains below.</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {form.provider === "turbo_smtp" && domains.length > 0 ? (
                <F label="From email (pick a whitelisted domain)">
                  <Select value={form.from_email} onValueChange={(v) => setForm({ ...form, from_email: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="smtp-from-domain-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{domains.map((d) => <SelectItem key={d.id} value={`no-reply@${d.domain}`}>no-reply@{d.domain}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              ) : (
                <F label="From email"><Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} required data-testid="smtp-from-email-input" /></F>
              )}
              <F label="From name"><Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Hourly limit"><Input type="number" value={form.hourly_limit} onChange={(e) => setForm({ ...form, hourly_limit: Number(e.target.value) })} /></F>
              <F label="Daily limit"><Input type="number" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: Number(e.target.value) })} /></F>
            </div>
            <DialogFooter><Button type="submit" className="rounded-xl" data-testid="smtp-submit">{editingId ? "Save" : "Create"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
